import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Button } from "@/components/ui/Button";
import { ArrowRightIcon, StopIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The question box.
 *
 * Enter sends, Shift+Enter is a newline (PRD M2). The textarea grows with its
 * content up to a ceiling so a pasted paragraph is readable without the box
 * eating the conversation.
 *
 * While a run is in flight the send button *becomes* Stop (PRD FR-4.6). The
 * only other Stop was in the live turn's header, which scrolls: a long trace
 * carries it off the top of the viewport, and the control for "stop spending
 * tokens on this" was then somewhere the reader had to go and find. The
 * composer is the one part of this page that never moves, and it is where a
 * reader's hands already are.
 */

const MAX_HEIGHT_PX = 220;

export function Composer({
  onSend,
  onStop,
  stopping,
  busy,
  disabled,
  placeholder,
  value,
  onChange,
  focusSignal = 0,
}: {
  onSend: (text: string) => void;
  /** Omitted where there is no run to end — the workbench reuses this box. */
  onStop?: () => void;
  stopping?: boolean;
  busy: boolean;
  disabled?: string | null;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Bumped by a caller that has just written a draft for the reader — "Ask
   * about California" (LINK-4) — so the box takes focus with the caret at the
   * end, ready to be edited or sent. Zero never focuses.
   */
  focusSignal?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || !focusSignal) return;
    node.focus();
    node.setSelectionRange(node.value.length, node.value.length);
  }, [focusSignal]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // An empty box keeps the height `rows={1}` gives it. Measuring on mount is
    // unreliable — the theme's type tokens arrive with a stylesheet, so a
    // first-paint `scrollHeight` can be measured against the wrong line height
    // and stick at the ceiling.
    if (!value) {
      node.style.height = "";
      return;
    }
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [value]);

  const send = () => {
    const text = value.trim();
    if (!text || busy || disabled) return;
    onSend(text);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "bg-card rounded-container border p-2",
          "transition-[border-color] motion-fast",
          focused ? "border-accent" : "border-border hover:border-border-emphasized",
        )}
      >
        <textarea
          ref={ref}
          rows={1}
          value={value}
          disabled={Boolean(disabled)}
          placeholder={disabled ?? placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-label={placeholder.replace(/…$/, "")}
          className={cn(
            "type-body text-ink placeholder:text-ink-disabled w-full resize-none bg-transparent",
            "px-2 py-2 outline-none disabled:cursor-not-allowed",
            // Past the ceiling the box scrolls rather than growing further.
            "overflow-y-auto",
          )}
        />
        <div className="flex items-center justify-between gap-3 px-2 pb-1">
          <span className="type-caption text-ink-disabled">
            {busy ? "Answering — Stop ends the run" : "Enter to send · Shift+Enter for a new line"}
          </span>
          {busy && onStop ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={onStop}
              disabled={stopping}
              icon={<StopIcon className="size-3.5" />}
            >
              {stopping ? "Stopping" : "Stop"}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              onClick={send}
              disabled={!value.trim() || busy || Boolean(disabled)}
              icon={<ArrowRightIcon className="size-3.5" />}
            >
              {busy ? "Working" : "Ask"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
